import { Listing, MealSummary } from '@tovala/browser-apis-combinedapi'
import { UserTerm } from 'types/internal'
import MealSelectionsConfirmationDialog, {
  AddOnPrompt,
  WantMoreMealsPrompt,
} from './MealSelectionsConfirmationDialog'
import ReleaseFromCartConfirmation from './ReleaseFromCartConfirmation'
import MenuFiltersConfirmationDialog from './MenuFiltersConfirmationDialog'
import NoListingFiltersConfirmationDialog from './NoListingFiltersConfirmationDialog'
import { useAddOns } from '@tovala/browser-apis-cdn'
import { useUser } from 'contexts/user'
import FamilyMealConfirmationDialog from './FamilyMealConfirmationDialog'

export type ConfirmationDialogData =
  | {
      meal: MealSummary
      onConfirm(): void
      type: 'releaseFromCart' | 'familyMealsWarning'
    }
  | {
      listing: Listing
      onConfirm(): void
      type: 'releaseListingFromCart'
    }
  | { type: 'filteredAllergens' }
  | { onConfirm(): void; type: 'noListingFilters' }
  | {
      isInfluencer: boolean
      openSource?: 'orderSummary'
      type: 'mealsSelected'
    }

const ConfirmationDialogs = ({
  confirmationDialogData,
  onClickViewSummary,
  onClickWantMoreMeals,
  onClose,
  onDecrementMeal,
  selectedUserTerm,
  totalOrderCount,
}: {
  confirmationDialogData: ConfirmationDialogData
  onClickViewSummary(): void
  onClickWantMoreMeals(): void
  onClose(): void
  onDecrementMeal(opts: { mealID: number }): void
  selectedUserTerm: UserTerm
  totalOrderCount: number | undefined
}) => {
  const addOn = useApplicableAddOn({
    termID: selectedUserTerm.termID,
    totalOrderCount: totalOrderCount ?? 0,
  })

  if (confirmationDialogData.type === 'mealsSelected') {
    return (
      <MealSelectionsConfirmationDialog
        nextActionPrompt={
          confirmationDialogData.isInfluencer ? null : addOn ? (
            <AddOnPrompt addOn={addOn} />
          ) : // If a user is at the largest subscription type, they can't add more meals so we
          // shouldn't show a prompt for them to add more.
          selectedUserTerm.nextLargestSubscriptionType ? (
            <WantMoreMealsPrompt
              onClickAddMoreMeals={() => {
                onClickWantMoreMeals()
                onClose()
              }}
            />
          ) : null
        }
        onClickViewSummary={onClickViewSummary}
        onCloseConfirmation={() => {
          onClose()
        }}
        openSource={confirmationDialogData.openSource}
        selectedUserTerm={selectedUserTerm}
      />
    )
  }

  if (confirmationDialogData.type === 'filteredAllergens') {
    return (
      <MenuFiltersConfirmationDialog
        onClose={() => {
          onClose()
        }}
      />
    )
  }

  if (confirmationDialogData.type === 'noListingFilters') {
    return (
      <NoListingFiltersConfirmationDialog
        onClose={() => {
          confirmationDialogData.onConfirm()
        }}
      />
    )
  }

  if (confirmationDialogData.type === 'releaseListingFromCart') {
    return (
      <ReleaseFromCartConfirmation
        onClickClose={() => {
          onClose()
        }}
        onClickConfirm={() => {
          confirmationDialogData.onConfirm()
          onClose()
        }}
      >
        <p>
          This extra is sold out. If you remove this extra, you may not be able
          to add it back.
        </p>
      </ReleaseFromCartConfirmation>
    )
  }

  const { onConfirm, meal, type } = confirmationDialogData

  if (type === 'familyMealsWarning') {
    return (
      <FamilyMealConfirmationDialog
        onClose={onClose}
        onConfirm={() => {
          onConfirm()
          onClose()
        }}
        trackMetadata={{
          mealID: meal.id,
          termID: selectedUserTerm.termID,
        }}
      />
    )
  }

  if (type === 'releaseFromCart') {
    return (
      <ReleaseFromCartConfirmation
        onClickClose={() => {
          onClose()
        }}
        onClickConfirm={() => {
          onDecrementMeal({ mealID: meal.id })
          onClose()
        }}
      >
        <p>
          This meal is sold out. If you remove this meal, you may not be able to
          add it back.
        </p>
      </ReleaseFromCartConfirmation>
    )
  }

  return null
}

export default ConfirmationDialogs

function useApplicableAddOn({
  termID,
  totalOrderCount,
}: {
  termID: number
  totalOrderCount: number
}) {
  const { user } = useUser()

  const { data: getAddOnsResponse } = useAddOns()
  const addOns = getAddOnsResponse?.addOns ?? []
  return totalOrderCount > 5
    ? addOns.find((addOn) => {
        return (
          addOn.termID === termID &&
          addOn.states.includes(user.shippingAddresses[0]?.state)
        )
      })
    : undefined
}
